import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Lightweight IntersectionObserver hook for reveal-on-scroll UI.
 * Fully client-side and safe for future reuse in larger apps.
 */
export default function useInView(options = {}) {
  const { root = null, rootMargin = '0px 0px -10% 0px', threshold = 0.15, once = true } = options;

  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  const observerOptions = useMemo(() => ({ root, rootMargin, threshold }), [root, rootMargin, threshold]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respect reduced motion: reveal immediately.
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (prefersReducedMotion) {
      setInView(true);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      const isIntersecting = Boolean(entry?.isIntersecting);
      if (isIntersecting) {
        setInView(true);
        if (once) observer.disconnect();
      } else if (!once) {
        setInView(false);
      }
    }, observerOptions);

    observer.observe(el);
    return () => observer.disconnect();
  }, [observerOptions, once]);

  return { ref, inView };
}
